/**
 * Extension API keys manager (B3-d). Issue / list / rotate / revoke the
 * tenant-scoped keys that let external tools call the Org Brain graph API.
 * The plaintext key is shown exactly ONCE on issue or rotate — the backend
 * stores only a hash — so we surface it in a copyable banner and never
 * again.
 */

import { useState } from 'react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import {
  IssuedApiKey,
  OrgBrainApiKey,
  useIssueApiKey,
  useOrgBrainApiKeys,
  useRevokeApiKey,
  useRotateApiKey,
} from './hooks';

interface Props {
  tenantId: string;
}

const SCOPES: { value: string; label: string; hint: string }[] = [
  { value: 'org-brain:read', label: 'Read-only', hint: 'view the graph' },
  { value: 'org-brain:admin', label: 'Admin', hint: 'view + manage' },
];

function plaintextOf(k: IssuedApiKey | undefined): string | undefined {
  return k?.plaintext_key ?? k?.api_key;
}

export default function OrgBrainApiKeys({ tenantId }: Props) {
  const { data: keys, isLoading } = useOrgBrainApiKeys(tenantId);
  const issue = useIssueApiKey(tenantId);
  const revoke = useRevokeApiKey(tenantId);
  const rotate = useRotateApiKey(tenantId);

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['org-brain:read']);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function toggleScope(s: string) {
    setScopes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  async function handleIssue() {
    setError(null);
    if (!name.trim()) {
      setError('Give the key a name so you can recognize it later.');
      return;
    }
    try {
      const res = await issue.mutateAsync({ name: name.trim(), scopes });
      setPlaintext(plaintextOf(res) ?? null);
      setName('');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to issue key');
    }
  }

  async function handleRotate(id: string) {
    setError(null);
    try {
      const res = await rotate.mutateAsync({ apiKeyId: id });
      setPlaintext(plaintextOf(res) ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to rotate key');
    }
  }

  async function handleRevoke(id: string) {
    setError(null);
    if (!confirm('Revoke this API key? Any integration using it will stop working.')) {
      return;
    }
    try {
      await revoke.mutateAsync(id);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to revoke key');
    }
  }

  return (
    <Card className="p-6 space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Extension API keys</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-xl">
          Give external tools scoped, read-only (or admin) access to this
          workspace's Org Brain graph. Keys are shown once — copy them
          somewhere safe.
        </p>
      </header>

      {plaintext && (
        <div className="rounded border border-emerald-300 bg-emerald-50 p-3">
          <div className="text-xs font-medium text-emerald-900">
            Copy this key now — it won't be shown again:
          </div>
          <code className="block mt-1 text-sm font-mono break-all text-emerald-900">
            {plaintext}
          </code>
          <button
            type="button"
            className="text-xs text-emerald-800 underline mt-1"
            onClick={() => {
              navigator.clipboard?.writeText(plaintext);
            }}
          >
            Copy to clipboard
          </button>
        </div>
      )}

      {/* Issue form */}
      <div className="border border-gray-200 rounded p-3 space-y-2">
        <div className="text-sm font-medium">Create a key</div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Key name (e.g. Reporting integration)"
            aria-label="API key name"
            className="border rounded px-3 py-2 text-sm flex-1 min-w-[200px]"
          />
          {SCOPES.map((s) => (
            <label key={s.value} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={scopes.includes(s.value)}
                onChange={() => toggleScope(s.value)}
              />
              <span>
                {s.label}{' '}
                <span className="text-xs text-gray-400">({s.hint})</span>
              </span>
            </label>
          ))}
          <Button onClick={handleIssue} disabled={issue.isPending || scopes.length === 0}>
            {issue.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">{error}</div>
      )}

      {/* Key list */}
      {isLoading ? (
        <Spinner />
      ) : !keys || keys.length === 0 ? (
        <div className="text-sm text-gray-500">No API keys yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Prefix</th>
                <th className="py-2 pr-3">Scopes</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k: OrgBrainApiKey) => {
                const revoked = Boolean(k.revoked_at);
                return (
                  <tr key={k.id} className="border-b border-gray-100">
                    <td className="py-2 pr-3">{k.name}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{k.key_prefix}…</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {k.scopes.join(', ')}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded ${
                          revoked
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {revoked ? 'Revoked' : 'Active'}
                      </span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {!revoked && (
                        <>
                          <button
                            type="button"
                            className="text-xs text-primary-700 hover:underline mr-3"
                            onClick={() => handleRotate(k.id)}
                            disabled={rotate.isPending}
                          >
                            Rotate
                          </button>
                          <button
                            type="button"
                            className="text-xs text-red-700 hover:underline"
                            onClick={() => handleRevoke(k.id)}
                            disabled={revoke.isPending}
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}